import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../helpers/api';

/**
 * How often to ask the server how a running import is getting on. Fast enough that a small file
 * feels immediate, slow enough that a 10,000-row commit does not generate hundreds of requests.
 */
const POLL_INTERVAL_MS = 1500;

const IN_FLIGHT = ['VALIDATING', 'COMMITTING'];

/**
 * Drives one bulk import from upload through preview to commit.
 *
 * Imports used to be a single synchronous request: the browser posted the whole file and waited.
 * On a real customer register that took minutes and, if the tab was refreshed in the meantime, the
 * data landed anyway while the user was told it had failed. The job model fixes that — this hook
 * holds the job id, polls while work is in flight, and exposes confirm/cancel — so a closed tab
 * loses nothing and a resumed page picks the same import back up.
 *
 * @param {object} params
 * @param {'ASSET'|'LOCATION'} params.entityType which importer
 * @param {function} [params.onCompleted] called once the job reaches COMPLETED, for reloading lists
 * @returns {object} job state and actions
 */
export default function useImportJob({ entityType, onCompleted }) {
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pollRef = useRef(null);
    const completedRef = useRef(null);
    const onCompletedRef = useRef(onCompleted);

    useEffect(() => {
        onCompletedRef.current = onCompleted;
    }, [onCompleted]);

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    /** Fires the completion callback exactly once per job, however the terminal state is observed. */
    const settle = useCallback((next) => {
        setJob(next);
        if (next?.status === 'COMPLETED' && completedRef.current !== next.id) {
            completedRef.current = next.id;
            onCompletedRef.current?.(next);
        }
    }, []);

    const fetchJob = useCallback(async (jobId) => {
        const { data } = await api.get(`import-job/${jobId}`);
        return data?.response ?? null;
    }, []);

    useEffect(() => {
        stopPolling();
        if (!job?.id || !IN_FLIGHT.includes(job.status)) {
            return undefined;
        }
        pollRef.current = setInterval(async () => {
            try {
                const next = await fetchJob(job.id);
                if (next) settle(next);
            } catch (e) {
                // A dropped poll is not a failed import — the job keeps running server-side. Stay
                // quiet and try again on the next tick rather than alarming the user.
                console.warn('Import job poll failed:', e?.message);
            }
        }, POLL_INTERVAL_MS);
        return stopPolling;
    }, [job?.id, job?.status, fetchJob, settle, stopPolling]);

    useEffect(() => stopPolling, [stopPolling]);

    /**
     * Uploads parsed rows and starts validation.
     *
     * @param {object} payload {fileName, rows}
     * @returns {Promise<object|null>} the created job
     */
    const start = useCallback(async (payload) => {
        setError('');
        setBusy(true);
        try {
            const { data } = await api.post(`import-job/${entityType.toLowerCase()}`, payload);
            const created = data?.response ?? null;
            settle(created);
            return created;
        } catch (e) {
            // 409 means an import is already open for this organization. Adopt it instead of
            // telling the user to come back later — almost always their own abandoned preview.
            const activeJobId = e?.response?.status === 409
                ? e?.response?.data?.error?.activeJobId
                : null;
            if (activeJobId) {
                const existing = await fetchJob(activeJobId);
                settle(existing);
                return existing;
            }
            setError(e?.response?.data?.message || e.message);
            return null;
        } finally {
            setBusy(false);
        }
    }, [entityType, fetchJob, settle]);

    /** Adopts a job that is already running, for the resume banner after a page reload. */
    const attach = useCallback(async (jobId) => {
        try {
            const next = await fetchJob(jobId);
            settle(next);
            return next;
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
            return null;
        }
    }, [fetchJob, settle]);

    /** Looks for an import of this user's worth resuming. Returns null when there is none. */
    const findResumable = useCallback(async () => {
        try {
            const { data } = await api.get('import-job/latest', { params: { entityType } });
            return data?.response ?? null;
        } catch (e) {
            console.warn('Could not check for a resumable import:', e?.message);
            return null;
        }
    }, [entityType]);

    /** Confirms the preview. Safe to call twice — the server treats a repeat as a no-op. */
    const commit = useCallback(async () => {
        if (!job?.id) return null;
        setError('');
        setBusy(true);
        try {
            const { data } = await api.post(`import-job/${job.id}/commit`);
            const next = data?.response ?? null;
            settle(next);
            return next;
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
            return null;
        } finally {
            setBusy(false);
        }
    }, [job?.id, settle]);

    /** Abandons the preview and drops the server-side copy of the sheet. */
    const cancel = useCallback(async () => {
        if (!job?.id) {
            setJob(null);
            return;
        }
        setBusy(true);
        try {
            await api.post(`import-job/${job.id}/cancel`);
        } catch (e) {
            console.warn('Could not cancel import job:', e?.message);
        } finally {
            setBusy(false);
            setJob(null);
            setError('');
        }
    }, [job?.id]);

    /** Full issue list, for the downloadable issues sheet. */
    const fetchAllIssues = useCallback(async () => {
        if (!job?.id) return [];
        const { data } = await api.get(`import-job/${job.id}/issues`);
        return data?.response ?? [];
    }, [job?.id]);

    /** Clears local state without touching the server (used when closing a finished import). */
    const reset = useCallback(() => {
        stopPolling();
        setJob(null);
        setError('');
    }, [stopPolling]);

    return { job, busy, error, start, attach, findResumable, commit, cancel, fetchAllIssues, reset, setError };
}
